import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const THEMES = {
  lumen:               { label: 'Lumen',               tagline: 'Clear, bright, effortless',       dark: false, classes: ['theme-lumen'] },
  umbra:               { label: 'Umbra',               tagline: 'Deep, focused, luminous',         dark: true,  classes: ['dark', 'theme-umbra'] },
  neobrutalism:        { label: 'Neo Brutalism',       tagline: 'Bold, loud, unapologetic',        dark: false, classes: ['theme-neobrutalism'] },
  'neobrutalism-dark': { label: 'Neo Brutalism Dark',  tagline: 'Loud, dark & neon',               dark: true,  classes: ['dark', 'theme-neobrutalism-dark'] },
  midnight:            { label: 'Midnight',            tagline: 'Deep, dark and focused',          dark: true,  classes: ['dark'] },
  daybreak:            { label: 'Daybreak',            tagline: 'Warm, natural light',             dark: false, classes: ['theme-daybreak'] },
  leet:                { label: 'L33T',                tagline: 'Old school matrix terminal',      dark: true,  classes: ['dark', 'theme-leet'] },
  sunset:              { label: 'Sunset Luxe',         tagline: 'Luxury deep purple vibes',        dark: true,  classes: ['dark', 'theme-sunset'] },
  arctic:              { label: 'Arctic',              tagline: 'Clean, crisp and icy cool',       dark: false, classes: ['theme-arctic'] },
  bayside:             { label: 'Bayside',             tagline: "Our Favorite High School's Colors", dark: false, classes: ['theme-bayside'] },
  win95:               { label: 'Retro95 OS',          tagline: 'Retro OS nostalgia trip',          dark: false, classes: ['theme-win95'] },
};

const ALL_THEME_CLASSES = ['dark', 'theme-lumen', 'theme-umbra', 'theme-daybreak', 'theme-neobrutalism', 'theme-neobrutalism-dark', 'theme-leet', 'theme-sunset', 'theme-arctic', 'theme-bayside', 'theme-win95'];

const STORAGE_KEYS = {
  theme: 'sparkseller-theme',
  defaultLight: 'sparkseller-default-light-theme',
  defaultDark: 'sparkseller-default-dark-theme',
};

const DEFAULT_LIGHT = 'lumen';
const DEFAULT_DARK = 'umbra';
const LEGACY_DEFAULT_LIGHT = 'neobrutalism';
const LEGACY_DEFAULT_DARK = 'neobrutalism-dark';

const isLightTheme = (key) => !!THEMES[key] && THEMES[key].dark === false;
const isDarkTheme  = (key) => !!THEMES[key] && THEMES[key].dark === true;

export const resolveThemePreference = (key) => {
  return THEMES[key] ? key : null;
};

const readSavedTheme = (storageKey, predicate, fallback, legacy) => {
  if (typeof window === 'undefined') return fallback;
  const saved = localStorage.getItem(storageKey);
  if (legacy && saved === legacy) return fallback;
  return (saved && predicate(saved)) ? saved : fallback;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_DARK;
    const saved = localStorage.getItem(STORAGE_KEYS.theme);
    // Keep an explicit choice, including Neo Brutalism. Only fall back
    // to Lumen/Umbra when nothing valid is saved.
    return (saved && THEMES[saved]) ? saved : DEFAULT_DARK;
  });

  const [defaultLightTheme, setDefaultLightThemeState] = useState(() =>
    readSavedTheme(STORAGE_KEYS.defaultLight, isLightTheme, DEFAULT_LIGHT, LEGACY_DEFAULT_LIGHT)
  );

  const [defaultDarkTheme, setDefaultDarkThemeState] = useState(() =>
    readSavedTheme(STORAGE_KEYS.defaultDark, isDarkTheme, DEFAULT_DARK, LEGACY_DEFAULT_DARK)
  );

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove(...ALL_THEME_CLASSES);
    const classes = THEMES[theme]?.classes || THEMES[DEFAULT_DARK].classes;
    root.classList.add(...classes);
    localStorage.setItem(STORAGE_KEYS.theme, theme);
  }, [theme]);

  // Guarded setters — silently ignore mode mismatches so a stray call can
  // never put a dark theme in the "light" slot or vice versa.
  const setDefaultLightTheme = (key) => {
    if (!isLightTheme(key)) return;
    setDefaultLightThemeState(key);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.defaultLight, key);
    }
  };

  const setDefaultDarkTheme = (key) => {
    if (!isDarkTheme(key)) return;
    setDefaultDarkThemeState(key);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.defaultDark, key);
    }
  };

  // Nav-bar Sun/Moon button: swap between the user's chosen default light
  // and default dark themes (falling back to the original hard-coded pair
  // if a stored value is somehow invalid).
  const toggleTheme = () => {
    const isDark = THEMES[theme]?.dark ?? true;
    if (isDark) {
      setTheme(isLightTheme(defaultLightTheme) ? defaultLightTheme : DEFAULT_LIGHT);
    } else {
      setTheme(isDarkTheme(defaultDarkTheme) ? defaultDarkTheme : DEFAULT_DARK);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        toggleTheme,
        themes: THEMES,
        defaultLightTheme,
        defaultDarkTheme,
        setDefaultLightTheme,
        setDefaultDarkTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
